import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Slider } from '@/components/ui/slider';
import { Info, Gauge } from 'lucide-react';
import { VideoFeatures } from '@/store/videoFeatures/types';
import { cn } from '@/lib/utils';

interface ManualSpeedTabProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  isAutomaticTestActive: boolean;
}

const speedMarkers = [1.0, 1.1, 1.2, 1.3, 1.4, 1.5];

export const ManualSpeedTab: React.FC<ManualSpeedTabProps> = ({
  features,
  setFeature,
  isAutomaticTestActive
}) => {
  useLanguage();
  // Velocidade fixa está ativa quando é diferente de 1.0 (normal)
  const isEnabled = features.playbackSpeed !== undefined && features.playbackSpeed !== 1.0;
  const currentSpeed = features.playbackSpeed ?? 1;

  return (
    <div className="space-y-4">
      {/* Info box quando teste automático está ativo */}
      {isAutomaticTestActive && (
        <div className="flex items-center gap-2 p-3 bg-[#F0F7FF] border border-[#D4E5FF] rounded-lg">
          <Info className="h-4 w-4 text-[#1F6BFF] flex-shrink-0" />
          <span className="text-xs text-[#1C1C1E]">
            {translateInterfaceText("Teste automático em andamento. A velocidade manual será aplicada quando o teste finalizar.")}</span>
        </div>
      )}

      {/* Card: Configuração de Velocidade Fixa */}
      <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-sm font-semibold text-[#1C1C1E]">{translateInterfaceText("Velocidade Fixa")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Toggle com ícone premium container */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
                isEnabled 
                  ? "bg-[#1D4FFF]/10 text-[#1D4FFF]" 
                  : "bg-[#E8F0FF] text-[#6B7280]"
              )}>
                <Gauge className="w-4 h-4" strokeWidth={2} />
              </div>
              <div>
                <span className="text-sm font-medium">{translateInterfaceText("Usar velocidade fixa")}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help inline-block ml-1.5" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-xs">
                        {translateInterfaceText("Escolha manualmente a velocidade de reprodução do vídeo.")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            <Switch
              checked={isEnabled}
              onCheckedChange={(enabled) => {
                // Quando desativa velocidade fixa, volta para 1.0 (normal) mas mantém o recurso ativo
                setFeature('playbackSpeed', enabled ? 1.2 : 1.0);
              }}
              className="data-[state=checked]:bg-[#1F6BFF]"
            />
          </div>

          {/* Slider de Velocidade Premium */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-[#1C1C1E]">{translateInterfaceText("Selecione a velocidade desejada")}</label>
              <span className="text-lg font-bold text-primary">{currentSpeed.toFixed(2)}x</span>
            </div>
            
            {/* Slider Container */}
            <div className="pt-2 pb-6">
              <Slider
                value={[currentSpeed]}
                onValueChange={(value) => setFeature('playbackSpeed', value[0])}
                min={1.0}
                max={1.5}
                step={0.05}
                disabled={!isEnabled}
                className="w-full"
              />
              
              {/* Marcadores visuais */}
              <div className="flex justify-between mt-3 px-0.5">
                {speedMarkers.map((marker) => (
                  <div key={marker} className="flex flex-col items-center">
                    <div className={cn(
                      "w-1 h-1 rounded-full mb-1.5 transition-colors",
                      Math.abs(currentSpeed - marker) < 0.03 
                        ? "bg-primary" 
                        : "bg-border"
                    )} />
                    <span className={cn(
                      "text-[11px] font-medium transition-colors",
                      Math.abs(currentSpeed - marker) < 0.03
                        ? "text-primary"
                        : "text-muted-foreground"
                    )}>
                      {marker.toFixed(1)}x
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Info box com velocidade atual */}
          <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/10">
            <span className="text-sm text-muted-foreground">{translateInterfaceText("Velocidade atual")}</span>
            <span className="text-lg font-semibold text-primary">{currentSpeed.toFixed(2)}x</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};